import { useState, useEffect } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { ArrowDown, WarningCircle, TrendUp, CheckCircle, Star, ChatCircleText, SmileyMeh, SmileySad, Smiley, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import useSiteContent from "../../lib/useSiteContent";
import { formatDate } from "../../lib/date";
import { CHART_COLORS, ACCENT, dT } from "./CsDashboard";

// ------------------------------------------------------------------ Trends
export function CsDashboardTrends({ data }) {
  const content = useSiteContent();
  const series = data.trends_by_demographics;
  const merged = {};
  series.forEach((s) => s.series.forEach((pt, i) => {
    merged[i] = merged[i] || { label: pt.label };
    merged[i][s.label] = pt.avg;
  }));
  const rows = Object.values(merged);
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-trends-by-demographics">
      <div className="text-sm font-medium text-slate-700 mb-1">{dT(content, "cs_dash_trends_title", "Trends by Demographics — Age Band")}</div>
      <p className="text-xs text-slate-400 mb-4">{dT(content, "cs_dash_trends_subtitle", "How each age group's average score has moved over the selected period.")}</p>
      <div style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 5, right: 12, bottom: 0, left: -20 }}>
            <CartesianGrid stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={24} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s, i) => (
              <Line key={s.label} type="monotone" dataKey={s.label} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 2.5 }} connectNulls={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {series.length === 0 && <div className="text-sm text-slate-400 text-center py-8">{dT(content, "cs_dash_trends_empty", "Not enough age-band data yet.")}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- Responses
export function ResponseFunnelCard({ data, isAdmin, testid = "cs-response-funnel", title }) {
  const content = useSiteContent();
  const emailsLocked = !isAdmin && !data.response_rows;
  const steps = data.funnel.filter((s) => (!s.admin_only || isAdmin) && !(emailsLocked && s.step === "Email Captured"));
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid={testid}>
      <div className="text-sm font-medium text-slate-700 mb-4">{title || dT(content, "cs_dash_funnel_title", "Response Funnel")}</div>
      <div className="flex flex-col items-center">
        {steps.map((s, i) => (
          <div key={s.step} className="w-full">
            <div className={`rounded-lg px-4 py-3 flex items-center justify-between ${s.admin_only ? "bg-slate-50 border border-dashed border-slate-300" : "bg-slate-100"}`} data-testid={`${testid}-step-${i}`}>
              <span className="text-sm font-medium text-slate-700">{s.step}{s.admin_only && <span className="ml-1.5 text-[10px] text-slate-400 font-normal">(internal)</span>}</span>
              <span className="text-lg font-bold text-slate-900">{s.n}</span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex items-center justify-center gap-1.5 py-1.5">
                <ArrowDown size={14} className="text-slate-300" />
                <span className="text-[11px] font-semibold text-slate-400">{steps[i + 1].pct}%</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const SENTIMENT_STYLE = {
  positive: { Icon: Smiley, cls: "text-emerald-600 bg-emerald-50" },
  neutral: { Icon: SmileyMeh, cls: "text-slate-500 bg-slate-100" },
  negative: { Icon: SmileySad, cls: "text-rose-600 bg-rose-50" },
};

// The "Action" a business should take on a respondent's email — see
// `_email_disclosure_reason` on the backend: reviews scoring <=3★ show up
// here for service recovery (an operational follow-up). Marketing opt-ins
// (any star rating) live entirely in their own separate "Marketing
// Opt-ins" section instead — no badge needed there, every row is already
// self-evidently an opt-in.
const EMAIL_REASON_STYLE = {
  service_recovery: { label: "Follow up", cls: "bg-red-50 text-red-600" },
};

function EmailReasonBadge({ reason }) {
  const st = EMAIL_REASON_STYLE[reason];
  if (!st) return null;
  return <span className={`inline-flex items-center text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid="cs-email-reason-badge">{st.label}</span>;
}

// "Customer Voice" — surfaces the AI sentiment/tag/summary analysis run on
// every open-ended question answer (only ever present when the business
// has at least one open-ended question AND a respondent actually wrote
// something). A comment with no ai_sentiment yet just hasn't finished its
// (few-second) background analysis pass — never an error state.
function CustomerVoiceCard({ voice, emailsVisible }) {
  const total = voice.analyzed_comments || 0;
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="cs-customer-voice">
      <div className="flex items-center gap-2 mb-1"><ChatCircleText size={16} style={{ color: ACCENT }} /><div className="text-sm font-medium text-slate-700">Customer Voice</div></div>
      <p className="text-xs text-slate-400 mb-4">What customers actually wrote, automatically read and tagged.</p>
      {voice.total_comments === 0 ? (
        <div className="text-sm text-slate-400 text-center py-6">No written comments in this period yet.</div>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            {["positive", "neutral", "negative"].map((s) => {
              const { Icon, cls } = SENTIMENT_STYLE[s];
              const n = voice.sentiment[s] || 0;
              const pct = total ? Math.round((100 * n) / total) : 0;
              return (
                <div key={s} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${cls}`} data-testid={`cs-voice-sentiment-${s}`}>
                  <Icon size={14} weight="fill" /> {pct}% {s}
                </div>
              );
            })}
          </div>
          {voice.top_tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="cs-voice-tags">
              {voice.top_tags.map(([tag, n]) => (
                <span key={tag} className="text-xs font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">{tag} · {n}</span>
              ))}
            </div>
          )}
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {voice.comments.map((c, i) => {
              const st = c.ai_sentiment ? SENTIMENT_STYLE[c.ai_sentiment] : null;
              return (
                <div key={i} className="border border-slate-100 rounded-lg p-3" data-testid={`cs-voice-comment-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="flex items-center gap-1 text-xs font-bold text-slate-900"><Star size={11} weight="fill" style={{ color: ACCENT }} />{c.avg_stars}</span>
                    {st && <st.Icon size={15} weight="fill" className={st.cls.split(" ")[0]} />}
                  </div>
                  <p className="text-sm text-slate-700 mb-1.5">"{c.comment_text}"</p>
                  {c.ai_summary && <p className="text-xs text-slate-400 italic mb-1.5">Summary: {c.ai_summary}</p>}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-slate-400">{c.gender}, {c.age_band} · {c.created_at ? formatDate(c.created_at) : "—"}{c.email ? ` · ${c.email}` : ""}</div>
                    {emailsVisible && c.email_reason === "service_recovery" && <EmailReasonBadge reason={c.email_reason} />}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function CsDashboardResponses({ data, isAdmin }) {
  const content = useSiteContent();
  const [deletedIds, setDeletedIds] = useState(new Set());
  const [deletingId, setDeletingId] = useState(null);
  const deleteResponse = async (id) => {
    if (!id || !window.confirm("Permanently delete this response? This cannot be undone. Their Oodel account (if any) is untouched.")) return;
    setDeletingId(id);
    try {
      await api.delete(`/admin/cs/responses/${id}`);
      setDeletedIds((prev) => new Set(prev).add(id));
      toast.success("Response deleted — regenerate this business's insight from the review queue if it needs updating.");
    } catch (e) {
      toast.error("Failed to delete response");
    } finally {
      setDeletingId(null);
    }
  };
  // When the business hasn't been granted email visibility, hide the
  // "Email Captured" funnel step and the whole Recent Respondents /
  // Marketing Opt-ins cards entirely — a locked teaser ("26 captured, ask
  // to unlock") would tempt the business with a number they can't act on,
  // which is worse than not showing the feature at all. Admin always sees
  // everything regardless.
  const emailsLocked = !isAdmin && !data.response_rows;
  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-md w-full"><ResponseFunnelCard data={data} isAdmin={isAdmin} /></div>
      {(data.voice.has_open_ended || data.voice.total_comments > 0) && (
        <CustomerVoiceCard voice={data.voice} emailsVisible={data.emails_visible} />
      )}
      {!emailsLocked && (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="cs-response-rows">
        <div className="text-sm font-medium text-slate-700 mb-1">{dT(content, "cs_dash_recent_respondents_title", "Critical Responses")}</div>
        <p className="text-xs text-slate-400 mb-4">{dT(content, "cs_dash_recent_respondents_subtitle", "Every respondent rating 3★ or below in the selected period — contact them for service recovery.")}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-slate-400 uppercase tracking-wide border-b border-slate-100">
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_email", "Email")}</th>
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_reason", "Action")}</th>
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_location", "Location")}</th>
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_rating", "Rating")}</th>
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_submitted", "Submitted")}</th>
              </tr>
            </thead>
            <tbody>
              {data.response_rows.slice(0, 50).map((r, i) => (
                <tr key={i} className="border-b border-slate-50" data-testid={`cs-response-row-${i}`}>
                  <td className="py-2 pr-4 text-slate-700">{r.email || "—"}</td>
                  <td className="py-2 pr-4"><EmailReasonBadge reason="service_recovery" /></td>
                  <td className="py-2 pr-4 text-slate-500">{r.location}</td>
                  <td className="py-2 pr-4 font-semibold text-slate-900">{r.avg_stars?.toFixed(1)}★</td>
                  <td className="py-2 pr-4 text-slate-400">{r.created_at ? formatDate(r.created_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.response_rows.length === 0 && <div className="text-sm text-slate-400 text-center py-8">{dT(content, "cs_dash_respondents_empty", "No critical responses in this period — nothing needs follow-up.")}</div>}
        </div>
      </div>
      )}
      {isAdmin && data.all_respondents && (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="cs-all-respondents">
        <div className="text-sm font-medium text-slate-700 mb-1">All Respondents <span className="text-[10px] text-slate-400 font-normal">(admin only)</span></div>
        <p className="text-xs text-slate-400 mb-4">Every response in the selected period with its email — regardless of star rating or marketing consent. Widen the date range above to see older scans too.</p>
        <div className="overflow-x-auto max-h-[420px]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-slate-400 uppercase tracking-wide border-b border-slate-100">
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Location</th>
                <th className="py-2 pr-4">Rating</th>
                <th className="py-2 pr-4">Marketing opt-in</th>
                <th className="py-2 pr-4">Submitted</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {data.all_respondents.filter((r) => !deletedIds.has(r.id)).map((r, i) => (
                <tr key={i} className="border-b border-slate-50" data-testid={`cs-all-respondent-row-${i}`}>
                  <td className="py-2 pr-4 text-slate-700">{r.email || "—"}</td>
                  <td className="py-2 pr-4 text-slate-500">{r.location}</td>
                  <td className="py-2 pr-4 font-semibold text-slate-900">{r.avg_stars?.toFixed(1)}★</td>
                  <td className="py-2 pr-4 text-slate-500">{r.marketing_opt_in ? "Yes" : "No"}</td>
                  <td className="py-2 pr-4 text-slate-400">{r.created_at ? formatDate(r.created_at) : "—"}</td>
                  <td className="py-2 pr-4">
                    <button onClick={() => deleteResponse(r.id)} disabled={deletingId === r.id} className="text-red-500 hover:text-red-700 disabled:opacity-40" data-testid={`cs-delete-response-${r.id}`} title="Delete this response">
                      <Trash size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.all_respondents.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No responses in this period.</div>}
        </div>
      </div>
      )}
      {!emailsLocked && data.marketing_optins && (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="cs-marketing-optins">
        <div className="text-sm font-medium text-slate-700 mb-1">{dT(content, "cs_dash_marketing_optins_title", "Marketing Opt-ins")}</div>
        <p className="text-xs text-slate-400 mb-4">{dT(content, "cs_dash_marketing_optins_subtitle", "Every respondent — any star rating — who checked \"Keep me updated on offers & news\". Safe to email with promos.")}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-slate-400 uppercase tracking-wide border-b border-slate-100">
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_email", "Email")}</th>
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_location", "Location")}</th>
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_rating", "Rating")}</th>
                <th className="py-2 pr-4">{dT(content, "cs_dash_respondents_col_submitted", "Submitted")}</th>
              </tr>
            </thead>
            <tbody>
              {data.marketing_optins.slice(0, 50).map((r, i) => (
                <tr key={i} className="border-b border-slate-50" data-testid={`cs-marketing-optin-row-${i}`}>
                  <td className="py-2 pr-4 text-slate-700">{r.email || "—"}</td>
                  <td className="py-2 pr-4 text-slate-500">{r.location}</td>
                  <td className="py-2 pr-4 font-semibold text-slate-900">{r.avg_stars?.toFixed(1)}★</td>
                  <td className="py-2 pr-4 text-slate-400">{r.created_at ? formatDate(r.created_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.marketing_optins.length === 0 && <div className="text-sm text-slate-400 text-center py-8">{dT(content, "cs_dash_marketing_optins_empty", "No marketing opt-ins in this period yet.")}</div>}
        </div>
      </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- Alerts
export function AlertsList({ alerts, limit, testid = "cs-alert" }) {
  const content = useSiteContent();
  const styleFor = (sev) => sev === "high" ? { Icon: WarningCircle, cls: "text-red-600 bg-red-50 border-red-100" }
    : sev === "good" ? { Icon: TrendUp, cls: "text-emerald-600 bg-emerald-50 border-emerald-100" }
    : { Icon: WarningCircle, cls: "text-amber-600 bg-amber-50 border-amber-100" };
  const rows = limit ? alerts.slice(0, limit) : alerts;
  return (
    <div className="space-y-2.5">
      {rows.map((a, i) => {
        const { Icon, cls } = styleFor(a.severity);
        return (
          <div key={i} className={`flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm ${cls}`} data-testid={`${testid}-${i}`}>
            <Icon size={16} weight="fill" className="shrink-0 mt-0.5" /> <span>{a.text}</span>
          </div>
        );
      })}
      {rows.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-slate-400"><CheckCircle size={16} /> {dT(content, "cs_dash_alerts_empty", "No alerts for this period — everything's steady.")}</div>
      )}
    </div>
  );
}

// Self-serve — the business (not admin) decides which star ratings should
// trigger an instant email + in-app bell notification. See
// `/business/me/alert-rules` and `cs_submit`'s alert-firing logic.
function AlertRulesPanel() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/business/me/alert-rules").then((r) => setRules(r.data)).catch(() => setRules({ enabled: false, stars: [] }));
  }, []);
  const toggleStar = (s) => setRules((r) => ({ ...r, stars: r.stars.includes(s) ? r.stars.filter((x) => x !== s) : [...r.stars, s].sort() }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/business/me/alert-rules", rules);
      setRules(r.data);
      toast.success("Alert settings saved.");
    } catch {
      toast.error("Failed to save alert settings.");
    } finally {
      setSaving(false);
    }
  };
  if (!rules) return null;
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-alert-rules-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-slate-700">Alert me by email &amp; notification</div>
        <Switch checked={rules.enabled} onCheckedChange={(v) => setRules((r) => ({ ...r, enabled: v }))} data-testid="cs-alert-rules-enabled" />
      </div>
      <p className="text-xs text-slate-400 mb-3">Get an instant email and bell notification whenever a new review matches the star ratings you pick below.</p>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s} type="button" onClick={() => toggleStar(s)} disabled={!rules.enabled} data-testid={`cs-alert-star-${s}`}
            className={`flex items-center gap-1 text-xs font-bold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-40 ${rules.stars.includes(s) ? "text-white border-transparent" : "text-slate-500 border-slate-200"}`}
            style={rules.stars.includes(s) ? { background: ACCENT } : {}}
          >
            {s} <Star size={11} weight="fill" />
          </button>
        ))}
      </div>
      <Button size="sm" onClick={save} disabled={saving} data-testid="cs-alert-rules-save" style={{ background: ACCENT }} className="text-white rounded-lg hover:opacity-90">
        {saving ? "Saving…" : "Save alert settings"}
      </Button>
    </div>
  );
}

function FlaggedReviewsList({ rows, emailsVisible }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-slate-400 text-center py-6">No flagged reviews in this period yet — turn on alert rules above to start tracking specific star ratings.</div>;
  }
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={i} className="flex items-start justify-between gap-3 border border-slate-100 rounded-lg px-3.5 py-2.5 text-sm" data-testid={`cs-flagged-review-${i}`}>
          <div>
            <span className="flex items-center gap-1 font-bold text-slate-900"><Star size={12} weight="fill" style={{ color: ACCENT }} />{r.avg_stars}</span>
            {r.comment_text && <p className="text-xs text-slate-500 mt-1">"{r.comment_text}"</p>}
          </div>
          <div className="text-right text-[11px] text-slate-400 shrink-0">
            <div>{r.gender}, {r.age_band}</div>
            <div>{r.created_at ? formatDate(r.created_at) : "—"}</div>
            {r.email && <div className="text-slate-600">{r.email}</div>}
            {emailsVisible && r.email_reason === "service_recovery" && <div className="mt-1"><EmailReasonBadge reason={r.email_reason} /></div>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CsDashboardAlerts({ data, isAdmin }) {
  const content = useSiteContent();
  return (
    <div className="space-y-5 max-w-2xl">
      {!isAdmin && <AlertRulesPanel />}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-alerts-panel">
        <div className="text-sm font-medium text-slate-700 mb-4">{dT(content, "cs_dash_alerts_title", "Alerts")}</div>
        <AlertsList alerts={data.alerts} />
      </div>
      {data.alert_rules?.stars?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-flagged-reviews-panel">
          <div className="text-sm font-medium text-slate-700 mb-1">Flagged Reviews</div>
          <p className="text-xs text-slate-400 mb-4">Reviews in this period matching your alert star ratings ({data.alert_rules.stars.map((s) => `${s}★`).join(", ")}).</p>
          <FlaggedReviewsList rows={data.flagged_reviews} emailsVisible={data.emails_visible} />
        </div>
      )}
    </div>
  );
}
